/**
 * Pure mappers from structured-ingest data to the recipe graph's nodes and edges.
 *
 * Two sources, one drawing: the *summary* (what the whole table becomes — counts on
 * nodes, edge counts per grain) and the *row lens* (what one row becomes — the
 * actual values, the cells they came from, the edge the row lands in and how many
 * rows share it). The words come from the caller's translator.
 */

import type { TFunction } from 'i18next'
import type { OutputSummary, RowExplanation } from '@/services/api/structuredIngest'

export type RecipeNodeState = 'on' | 'off' | 'new' | 'missing'
export type RecipeEdgeState = 'on' | 'off' | 'new' | 'dim'

export interface RecipeGraphNode {
  id: string
  /** The big line: the entity's name (summary) or the row's value (row lens). */
  title: string
  /** The small line under it: what kind of thing it is, or where it came from. */
  subtitle?: string
  /** Schema label — drives icon and colour. */
  label: string
  /** Right-aligned chip: "25", "off", "not in this row". */
  badge?: string
  source: 'subject' | 'row'
  state: RecipeNodeState
  /** Row lens: the cells the identity was read from. */
  cells?: Array<{ column: string; value: string }>
}

export interface RecipeGraphEdge {
  id: string
  source: string
  target: string
  type: string
  kind: 'structural' | 'event_aggregate'
  /** "25 relationships" or "1 of 48 rows". */
  badge?: string
  /** "one per pair, Σ Částka CZK" or "OUT, TRVALÝ PŘÍKAZ". */
  detail?: string
  state: RecipeEdgeState
}

export interface RecipeGraphData {
  nodes: RecipeGraphNode[]
  edges: RecipeGraphEdge[]
}

const CATEGORY = 'category:'

/** What makes two rows land in the same relationship, read from a grain string. */
export type Grain =
  | { kind: 'pair' | 'direction' | 'month' }
  | { kind: 'category'; column: string }
  | { kind: 'other'; grain: string }

export function parseGrain(grain: string): Grain {
  if (grain.startsWith(CATEGORY)) return { kind: 'category', column: grain.slice(CATEGORY.length) }
  if (grain === 'pair' || grain === 'direction' || grain === 'month') return { kind: grain }
  return { kind: 'other', grain }
}

/** "one per pair and month": what makes two rows land in the same relationship. */
export function grainPhrase(grain: string, t: TFunction): string {
  const parsed = parseGrain(grain)
  if (parsed.kind === 'category') return t('tableMapping.grain.category', { column: parsed.column })
  if (parsed.kind === 'other') return t('tableMapping.grain.other', { grain: parsed.grain })
  return t(`tableMapping.grain.${parsed.kind}`)
}

/** The same choice as a chip after "one relationship per": "pair and month". */
export function grainChip(grain: string, t: TFunction): string {
  const parsed = parseGrain(grain)
  if (parsed.kind === 'category')
    return t('tableMapping.grain.chip.category', { column: parsed.column })
  if (parsed.kind === 'other') return parsed.grain
  return t(`tableMapping.grain.chip.${parsed.kind}`)
}

/** The whole table as a graph: counts on nodes, edge counts at the chosen grain. */
export function graphFromSummary(summary: OutputSummary, t: TFunction): RecipeGraphData {
  const nodes: RecipeGraphNode[] = summary.nodes.map(n => ({
    id: n.name,
    title: n.name,
    subtitle:
      n.source === 'subject'
        ? t('tableMapping.graph.sameOnEveryRow')
        : t('tableMapping.graph.fromEachRow'),
    label: n.label,
    badge: !n.enabled
      ? t('tableMapping.graph.off')
      : n.source === 'subject'
        ? '1'
        : n.count === null
          ? '?'
          : n.count.toLocaleString(),
    source: n.source,
    state: !n.enabled ? 'off' : n.outsideSchema ? 'new' : 'on',
  }))
  const known = new Set(nodes.map(n => n.id))
  const edges: RecipeGraphEdge[] = summary.edges
    .filter(e => known.has(e.from) && known.has(e.to))
    .map(e => ({
      id: e.name,
      source: e.from,
      target: e.to,
      type: e.type,
      kind: e.kind,
      badge:
        e.count === null
          ? e.kind === 'structural'
            ? t('tableMapping.grain.fact')
            : t('tableMapping.graph.afterUpdate')
          : t('tableMapping.count.relationships', { count: e.count }),
      detail: [
        e.kind === 'structural' ? t('tableMapping.grain.fact') : grainPhrase(e.grain, t),
        ...e.measures.map(m => `Σ ${m}`),
      ].join(', '),
      state: !e.enabled ? 'off' : e.outsideSchema ? 'new' : 'on',
    }))
  return { nodes, edges }
}

function rowNodeSubtitle(e: RowExplanation['entities'][number], t: TFunction): string {
  if (e.enabled === false) return t('tableMapping.graph.excluded', { name: e.name })
  if (e.source === 'subject') return t('tableMapping.graph.subjectOfFile')
  if (e.missing) return t('tableMapping.graph.noIdentifier')
  return e.name
}

function rowEdgeBadge(r: RowExplanation['relationships'][number], t: TFunction): string {
  if (r.enabled === false) return t('tableMapping.graph.off')
  if (!r.in_scope) return t('tableMapping.graph.notThisRow')
  return r.kind === 'structural'
    ? t('tableMapping.count.rows', { count: r.edge_rows })
    : t('tableMapping.graph.oneOfRows', { count: r.edge_rows })
}

/** One row as a graph: the values it yields, the cells they came from, the link it lands in. */
export function graphFromRow(explanation: RowExplanation, t: TFunction): RecipeGraphData {
  const nodes: RecipeGraphNode[] = explanation.entities.map(e => {
    const enabled = e.enabled !== false
    return {
      id: e.name,
      title: e.display || e.key || e.name,
      subtitle: rowNodeSubtitle(e, t),
      label: e.label,
      badge: !enabled
        ? t('tableMapping.graph.off')
        : e.missing
          ? t('tableMapping.graph.notInRow')
          : undefined,
      source: e.source,
      state: !enabled ? 'off' : e.missing ? 'missing' : 'on',
      cells: Object.entries(e.cells).map(([column, value]) => ({ column, value })),
    }
  })
  const known = new Set(nodes.map(n => n.id))
  const edges: RecipeGraphEdge[] = explanation.relationships
    .filter(r => known.has(r.from) && known.has(r.to))
    .map(r => {
      const grain = Object.values(r.grain).filter(Boolean).join(', ')
      const measures = Object.entries(r.measures)
        .filter(([, v]) => v !== null)
        .map(([m, v]) => `${m}: ${(v as number).toLocaleString()}`)
        .join(', ')
      return {
        id: r.name,
        source: r.from,
        target: r.to,
        type: r.type,
        kind: r.kind,
        badge: rowEdgeBadge(r, t),
        detail:
          [
            r.in_scope && r.kind !== 'structural' ? t('tableMapping.graph.rowIsOne') : '',
            grain,
            measures,
          ]
            .filter(Boolean)
            .join(', ') || undefined,
        state: r.enabled === false ? 'off' : r.in_scope ? 'on' : 'dim',
      }
    })
  return { nodes, edges }
}
